import { useFeatureFlagVariantKey, usePostHog } from 'posthog-js/react'
import { useEffect, useMemo, useState } from 'react'
import { FEATURE_FLAGS, useAnnualPlan, usePricing } from '../../hooks/featureFlags'
import { logGoogleAdsEvent } from '../../helpers/analytics'
import { useDispatch } from 'react-redux'
import { useLocalStorage } from '../../helpers/utility'

const useCheckoutParams = () => {
	const [suggestMonthlyModalOpen, setSuggestMonthlyModalOpen] = useState(false)
	const [subscriptionModalOpen, setSubscriptionModalOpen] = useState(false)
	const [thanksModalOpen, setThanksModalOpen] = useState(false)
	const [checkoutStep, setCheckoutStep] = useState<'selector' | 'checkout' | 'success'>('selector')
	const [annualPlan, setAnnualPlan] = useAnnualPlan()
	const posthog = usePostHog()
	const monthlySuggestionFF = useFeatureFlagVariantKey(FEATURE_FLAGS.MONTHLY_SUGGESTION)
	const price = usePricing()
	const [pageRefreshed, setPageRefreshed] = useLocalStorage('succeessfulPaymentReloads', false)
	const dispatch = useDispatch()

	const searchParamsString = document.location.search

	const searchParams = useMemo(() => {
		let params = new URLSearchParams(searchParamsString)
		return {
			canceled: params.getAll('canceled')[0],
			checkoutComplete: params.getAll('checkout_complete')[0],
			showModal: params.getAll('showModal')[0],
			paddleSuccess: params.getAll('paddleSuccess')[0],
			annual: params.getAll('annual')[0],
		}
	}, [searchParamsString])

	const capturePurchase = () => {
		posthog?.capture('purchase', {
			price: annualPlan ? price.yearlyTotal : price.monthly,
			plan: annualPlan ? 'annual' : 'monthly',
		})
		posthog?.capture('subscribe', {
			price: annualPlan ? price.yearlyTotal : price.monthly,
			plan: annualPlan ? 'annual' : 'monthly',
		})
		logGoogleAdsEvent('purchase')
	}

	useEffect(() => {
		if (searchParams.showModal) {
			setThanksModalOpen(true)
			const newSearchParams = new URLSearchParams(searchParamsString)
			newSearchParams.delete('showModal')
			const newUrl = window.location.pathname + (newSearchParams.toString() ? '?' + newSearchParams.toString() : '')
			window.history.replaceState(null, '', newUrl)
			capturePurchase()
		}
	}, [searchParams, dispatch])

	useEffect(() => {
		if (searchParams.paddleSuccess && pageRefreshed) {
			setThanksModalOpen(true)
			const newSearchParams = new URLSearchParams(searchParamsString)
			newSearchParams.delete('paddleSuccess')
			const newUrl = window.location.pathname + (newSearchParams.toString() ? '?' + newSearchParams.toString() : '')
			window.history.replaceState(null, '', newUrl)
			capturePurchase()
		}
	}, [searchParams, dispatch])

	useEffect(() => {
		if (searchParams.paddleSuccess && !pageRefreshed) {
			setPageRefreshed(true)
			window.location.reload()
		}
	}, [searchParams, pageRefreshed])

	useEffect(() => {
		if (searchParams.checkoutComplete) {
			setCheckoutStep('success')
			setSubscriptionModalOpen(true)
			capturePurchase()
		}
	}, [searchParams.checkoutComplete])

	useEffect(() => {
		if (searchParams.canceled) {
			posthog?.capture('checkout-canceled', { annual: annualPlan })
			if (annualPlan && monthlySuggestionFF === 'test' && !subscriptionModalOpen) {
				setSuggestMonthlyModalOpen(true)
				setAnnualPlan(false)
			} else if (!suggestMonthlyModalOpen && !!monthlySuggestionFF) {
				setSubscriptionModalOpen(true)
			}
		}
	}, [searchParams.canceled, monthlySuggestionFF, annualPlan])

	return {
		checkoutStep,
		setCheckoutStep,
		suggestMonthlyModalOpen,
		subscriptionModalOpen,
		setSubscriptionModalOpen,
		setSuggestMonthlyModalOpen,
		thanksModalOpen,
		setThanksModalOpen,
	}
}

export default useCheckoutParams
